import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const EMPTY_SUMMARY: TIntegrityLiveSummary = {
  active_participants: 0,
  away_participants: 0,
  total_away_events: 0,
  total_away_duration_ms: 0,
  interval_snapshot_count: 0,
  tab_away_snapshot_count: 0,
  snapshot_failed_count: 0,
};

export function safeNumber(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

export function formatIntegrityDuration(value: unknown): string {
  const totalSeconds = Math.floor(safeNumber(value) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours} jam ${minutes} menit`;
  if (minutes > 0) return `${minutes} menit ${seconds} detik`;
  return `${seconds} detik`;
}

export function formatIntegrityTime(value?: string | null): string {
  if (!value) return "Belum ada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Belum ada";
  return date.toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function normalizeLiveResponse(data: unknown): TIntegrityLiveResponse {
  const response = (data ?? {}) as Partial<TIntegrityLiveResponse>;
  const summary = response.summary ?? EMPTY_SUMMARY;
  return {
    summary: {
      active_participants: safeNumber(summary.active_participants),
      away_participants: safeNumber(summary.away_participants),
      total_away_events: safeNumber(summary.total_away_events),
      total_away_duration_ms: safeNumber(summary.total_away_duration_ms),
      interval_snapshot_count: safeNumber(summary.interval_snapshot_count),
      tab_away_snapshot_count: safeNumber(summary.tab_away_snapshot_count),
      snapshot_failed_count: safeNumber(summary.snapshot_failed_count),
    },
    list: Array.isArray(response.list) ? response.list : [],
  };
}

export function indexLiveRows(rows: TIntegrityLiveRow[]) {
  const index = new Map<string, TIntegrityLiveRow>();
  rows.forEach((row) => {
    const kind = String(row.session_kind).toLowerCase();
    index.set(`${row.participant_id}:${kind}:${row.session_id}`, row);
    // Fallback when PIC participant list omits id (legacy API payloads).
    index.set(`${kind}:${row.session_id}`, row);
    index.set(`session:${row.session_id}`, row);
  });
  return index;
}

export function lookupLiveRow(
  bySession: Map<string, TIntegrityLiveRow>,
  participantId: number | string | null | undefined,
  sessionKind: string,
  sessionId: number | string | null | undefined
): TIntegrityLiveRow | undefined {
  if (sessionId === null || sessionId === undefined || sessionId === "") {
    return undefined;
  }
  const kind = String(sessionKind).toLowerCase();
  if (participantId !== null && participantId !== undefined && participantId !== "") {
    const exact = bySession.get(`${participantId}:${kind}:${sessionId}`);
    if (exact) return exact;
  }
  return (
    bySession.get(`${kind}:${sessionId}`) ??
    bySession.get(`session:${sessionId}`)
  );
}

export function useIntegrityLive(
  assessmentId: number | string | undefined,
  load: (assessmentId: string) => Promise<{ data: unknown }>,
  intervalMs = 4000
) {
  const [data, setData] = useState<TIntegrityLiveResponse>({
    summary: EMPTY_SUMMARY,
    list: [],
  });
  const [available, setAvailable] = useState(true);
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const loadedOnceRef = useRef(false);
  const loadRef = useRef(load);

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    loadedOnceRef.current = false;
    setAvailable(true);
    setData({ summary: EMPTY_SUMMARY, list: [] });
  }, [assessmentId]);

  const refresh = useCallback(async () => {
    if (assessmentId === undefined || loadingRef.current) return;
    loadingRef.current = true;
    try {
      const response = await loadRef.current(String(assessmentId));
      if (mountedRef.current) {
        setData(normalizeLiveResponse(response.data));
        setAvailable(true);
        loadedOnceRef.current = true;
      }
    } catch {
      // Keep last good snapshot after a successful load; only flag unavailable on first failure.
      if (mountedRef.current && !loadedOnceRef.current) {
        setAvailable(false);
      }
    } finally {
      loadingRef.current = false;
    }
  }, [assessmentId]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), intervalMs);
    return () => window.clearInterval(interval);
  }, [intervalMs, refresh]);

  const bySession = useMemo(() => indexLiveRows(data.list), [data.list]);
  return { ...data, bySession, available, refresh };
}

export function IntegritySummaryStrip({
  summary,
  available = true,
}: {
  summary: TIntegrityLiveSummary;
  available?: boolean;
}) {
  if (!available) {
    return (
      <div className="mb-4 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-600 ring-1 ring-inset ring-gray-200">
        Data integritas belum tersedia.
      </div>
    );
  }
  const items = [
    ["Peserta aktif", safeNumber(summary.active_participants).toString()],
    ["Sedang keluar", safeNumber(summary.away_participants).toString()],
    ["Kejadian pindah tab", safeNumber(summary.total_away_events).toString()],
    ["Total durasi", formatIntegrityDuration(summary.total_away_duration_ms)],
  ];
  return (
    <dl className="mb-4 grid grid-cols-2 overflow-hidden rounded-lg border border-gray-200 bg-white sm:grid-cols-4">
      {items.map(([label, value]) => (
        <div key={label} className="border-b border-gray-100 px-4 py-3 sm:border-b-0 sm:border-r last:border-0">
          <dt className="text-xs font-medium text-gray-500">{label}</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ParticipantIntegrityState({
  row,
}: {
  row?: TIntegrityLiveRow;
}) {
  if (!row) {
    return <span className="text-xs text-gray-500">Belum ada aktivitas integritas</span>;
  }
  return (
    <div className="space-y-1 text-xs text-gray-600">
      <span
        className={
          "inline-flex rounded-full px-2 py-0.5 font-medium ring-1 ring-inset " +
          (row.is_away
            ? "bg-amber-50 text-amber-800 ring-amber-600/20"
            : "bg-green-50 text-green-700 ring-green-600/20")
        }
      >
        {row.is_away ? "Sedang di luar halaman" : "Berada di halaman"}
      </span>
      <p>
        {safeNumber(row.away_count)} kali · terakhir {formatIntegrityTime(row.last_event_at)}
      </p>
      {row.is_away && (
        <>
          <p>Keluar sejak {formatIntegrityTime(row.away_since)}</p>
          <p>
            Durasi saat ini:{" "}
            {formatIntegrityDuration(
              row.away_since
                ? Math.max(0, Date.now() - new Date(row.away_since).getTime())
                : 0
            )}
          </p>
        </>
      )}
    </div>
  );
}

export const EMPTY_INTEGRITY_REPORT: TIntegrityReportSummary = {
  away_count: 0,
  total_away_duration_ms: 0,
  longest_away_duration_ms: 0,
  fullscreen_exit_count: 0,
  blocked_paste_count: 0,
  snapshot_success_count: 0,
  snapshot_failed_count: 0,
  interval_snapshot_count: 0,
  tab_away_snapshot_count: 0,
  consent_accepted_at: null,
  events: [],
  snapshots: [],
};

export function normalizeIntegrityReport(
  data: unknown
): TIntegrityReportSummary | undefined {
  const row = (data as {
    integrity_summary?: Partial<TIntegrityReportSummary>;
  })?.integrity_summary;
  if (!row) return undefined;
  return {
    ...EMPTY_INTEGRITY_REPORT,
    ...row,
    away_count: safeNumber(row.away_count),
    total_away_duration_ms: safeNumber(row.total_away_duration_ms),
    longest_away_duration_ms: safeNumber(row.longest_away_duration_ms),
    fullscreen_exit_count: safeNumber(row.fullscreen_exit_count),
    blocked_paste_count: safeNumber(row.blocked_paste_count),
    snapshot_success_count: safeNumber(row.snapshot_success_count),
    snapshot_failed_count: safeNumber(row.snapshot_failed_count),
    interval_snapshot_count: safeNumber(row.interval_snapshot_count),
    tab_away_snapshot_count: safeNumber(row.tab_away_snapshot_count),
    events: Array.isArray(row.events) ? row.events : [],
    snapshots: Array.isArray(row.snapshots) ? row.snapshots : [],
  };
}

export function IntegrityReportSection({
  summary,
  showSnapshots,
  snapshotBasePath,
}: {
  summary?: TIntegrityReportSummary;
  showSnapshots: boolean;
  snapshotBasePath: string;
}) {
  if (!summary) {
    return (
      <section className="my-4 break-inside-avoid border border-gray-300 px-4 py-3">
        <h2 className="text-lg font-semibold">Ringkasan integritas sesi</h2>
        <p className="mt-2 text-sm text-gray-600">
          Sesi ini tidak dipantau atau data integritas belum tersedia.
        </p>
      </section>
    );
  }
  const row = summary;
  const awayEvents = row.events.filter((event) => event.event_type === "tab_away");
  const eventSnapshots = row.snapshots.filter(
    (snapshot) =>
      snapshot.capture_reason === "tab_away" &&
      snapshot.status === "saved" &&
      snapshot.file_path
  );
  return (
    <section className="my-4 break-inside-avoid border border-gray-300 px-4 py-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">Ringkasan integritas sesi</h2>
        <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700 ring-1 ring-inset ring-cyan-600/20">
          Catatan pendukung
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <div><dt className="text-gray-500">Pindah tab</dt><dd className="font-semibold">{safeNumber(row.away_count)} kali</dd></div>
        <div><dt className="text-gray-500">Total durasi</dt><dd className="font-semibold">{formatIntegrityDuration(row.total_away_duration_ms)}</dd></div>
        <div><dt className="text-gray-500">Durasi terlama</dt><dd className="font-semibold">{formatIntegrityDuration(row.longest_away_duration_ms)}</dd></div>
        <div><dt className="text-gray-500">Keluar layar penuh</dt><dd className="font-semibold">{safeNumber(row.fullscreen_exit_count)} kali</dd></div>
        <div><dt className="text-gray-500">Snapshot saat pindah tab</dt><dd className="font-semibold">{safeNumber(row.tab_away_snapshot_count)}</dd></div>
        <div><dt className="text-gray-500">Snapshot gagal</dt><dd className="font-semibold">{safeNumber(row.snapshot_failed_count)}</dd></div>
      </dl>
      <div className="mt-3 text-sm">
        <p className="font-medium text-gray-700">Waktu pindah tab</p>
        {awayEvents.length === 0 ? (
          <p className="mt-1 text-gray-500">Tidak ada kejadian pindah tab.</p>
        ) : (
          <ul className="mt-1 list-disc pl-5 text-gray-600">
            {awayEvents.map((event) => (
              <li key={event.client_event_id}>
                {formatIntegrityTime(event.occurred_at)} · {formatIntegrityDuration(event.duration_ms)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {showSnapshots && eventSnapshots.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-gray-700">Snapshot saat peserta pindah tab</p>
          <div className="flex flex-wrap gap-2">
            {eventSnapshots.map((snapshot) => (
              <figure key={snapshot.id} className="w-40 border border-amber-200 bg-amber-50 p-1">
                <img
                  className="w-full"
                  alt="Snapshot saat pindah tab"
                  src={`${snapshotBasePath}/${String(snapshot.file_path).replace(/\\/g, "/")}`}
                />
                <figcaption className="mt-1 text-center text-xs text-amber-900">
                  Pindah tab · {formatIntegrityTime(snapshot.created_at)}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
